import { memo, useMemo } from 'react';
import { Box, Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { FieldValue, QueryRule } from '../../../../../types/QueryBuilder';
import { useLanguage } from '../../../../../context/LanguageContext';
import ChipsSelectEditor from './ChipsSelectEditor';
import TriStateToggle from './TriStateToggle';
import { setSelected, toStringList } from './valueUtils';

interface CheckboxEditorProps {
    ruleId: string;
    value: QueryRule['value'];
    values: FieldValue[];
    onChange: (value: string | string[]) => void;
    compact?: boolean;
    fillHeight?: boolean;
}

const NO_PREFERENCE_VALUE = '';

const COMPACT_GRID_SX = { display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, columnGap: 1 };
const TOOLTIP_LABEL_SX = { display: 'flex', alignItems: 'center', gap: 0.5 };
const TOOLTIP_ICON_SX = { fontSize: '1rem', color: 'text.secondary' };
const FORM_CONTROL_LABEL_SX = { m: 0 };

const namesOf = (option: FieldValue): string[] => option.mergedNames ?? [option.name];

function fillHeightGridSx(rowCount: number) {
    return {
        ...COMPACT_GRID_SX,
        height: '100%',
        alignItems: 'center',
        gridAutoFlow: { xs: 'row', sm: 'column' },
        gridTemplateRows: { xs: 'none', sm: `repeat(${rowCount}, 1fr)` },
        gridAutoRows: { xs: '1fr', sm: 'auto' },
    };
}

function OptionLabel({ option }: { option: FieldValue }) {
    if (!option.tooltip) return <>{option.label}</>;
    return (
        <Box sx={TOOLTIP_LABEL_SX}>
            {option.label}
            <Tooltip title={option.tooltip} arrow>
                <InfoOutlinedIcon sx={TOOLTIP_ICON_SX} />
            </Tooltip>
        </Box>
    );
}

function CheckboxEditor({ ruleId, value, values, onChange, compact = false, fillHeight = false }: CheckboxEditorProps) {
    const { t } = useLanguage();
    const selectedValues = useMemo(() => toStringList(value), [value]);
    const selectedSet = useMemo(() => new Set(selectedValues), [selectedValues]);

    if (values.length === 0) {
        return (
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <TriStateToggle
                    value={String(value ?? NO_PREFERENCE_VALUE)}
                    noPreferenceValue={NO_PREFERENCE_VALUE}
                    labels={{
                        accept: t('pages.clients.queryBuilder.simple.optin.accepts'),
                        refuse: t('pages.clients.queryBuilder.simple.optin.refuses'),
                        noPreference: t('pages.clients.queryBuilder.simple.optin.noPreference'),
                    }}
                    onChange={next => onChange(next ?? NO_PREFERENCE_VALUE)}
                />
            </Box>
        );
    }

    if (!compact) {
        return <ChipsSelectEditor ruleId={ruleId} values={values} selectedValues={selectedValues} onChange={onChange} />;
    }

    return (
        <Box sx={fillHeight ? fillHeightGridSx(Math.ceil(values.length / 2)) : COMPACT_GRID_SX}>
            {values.map(option => (
                <FormControlLabel
                    key={option.name}
                    sx={FORM_CONTROL_LABEL_SX}
                    control={
                        <Checkbox
                            checked={namesOf(option).some(name => selectedSet.has(name))}
                            onChange={(e) => onChange(setSelected(selectedValues, namesOf(option), e.target.checked))}
                            size="small"
                        />
                    }
                    label={<OptionLabel option={option} />}
                />
            ))}
        </Box>
    );
}

export default memo(CheckboxEditor);
